import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { useDebounce } from "@/hooks/use-debounce";
import { absoluteUrl } from "@/lib/utils";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import { setFlightTimer } from "@/feature/flight/checkout/state/revalidate.slice";
import { sseSearchParamsFormatter } from "@/feature/flight/search/lib/format-search-params";
import {
  resetFlightFilter,
  setFilters,
  setFlightResult,
} from "@/feature/flight/search/state/slices/extract-flight-filter.slice";
import { resetSelectedFlight } from "@/feature/flight/search/state/slices/flight-data.slice";
import {
  setError,
  setIsResponseEnd,
  setLoading,
  setSearchKey,
  toggleModifyBar,
} from "@/feature/flight/search/state/slices/flight-search-status.slice";
import { IFlight, ISearchInfo } from "@/feature/flight/search/types/flight";
import { TFlightSearchFormData } from "@/feature/flight/search/types/search";

const useFlightSearchSSE = ({
  formData,
}: {
  formData: TFlightSearchFormData;
}) => {
  const dispatch = useAppDispatch();
  const eventSourceRef = useRef<EventSource | null>(null);
  const [flightData, setFlightData] = useState<IFlight[]>([]);
  const [searchInfo, setSearchInfo] = useState<ISearchInfo | null>(null);
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  const completedRef = useRef(false); // Track if search completed successfully
  const activeAttemptRef = useRef<string>("");
  const failedAttemptRef = useRef<string | null>(null);
  const hasHandledErrorRef = useRef(false);
  const debouncedFlightData = useDebounce(flightData, 120);

  const { connectionKey, searchKey: storedSearchKey, isResponseEnd } =
    useAppSelector((state) => state.flightSearchStatus);
  const flightTimer = useAppSelector((state) => state.flightRevalidate.flightTimer);

  // Generate a stable search key from form data
  const currentSearchKey = useMemo(
    () => sseSearchParamsFormatter(formData),
    [formData],
  );
  const currentAttemptKey = useMemo(
    () => `${currentSearchKey}::${connectionKey ?? "default"}`,
    [currentSearchKey, connectionKey],
  );
  const isCacheStillValid =
    typeof flightTimer === "number" && flightTimer > currentTime;

  useEffect(() => {
    if (typeof flightTimer !== "number") {
      return;
    }

    const intervalId = window.setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [flightTimer]);

  // Memoized cleanup function
  const cleanup = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  // Handle SSE errors
  const handleError = useCallback(
    (errorMessage: string) => {
      // Don't show error if we already completed successfully
      if (completedRef.current || hasHandledErrorRef.current) return;

      hasHandledErrorRef.current = true;
      failedAttemptRef.current = activeAttemptRef.current;

      toast.error(errorMessage);
      cleanup();

      dispatch(setError(true));
      dispatch(setLoading(false));
      dispatch(setIsResponseEnd(true));
      dispatch(toggleModifyBar(true));
      dispatch(resetSelectedFlight());
    },
    [cleanup, dispatch],
  );

  // Process incoming flight data
  const processFlightData = useCallback((newFlight: IFlight) => {
    const getTotalLayoverTime = (flight: IFlight) =>
      flight.flights.reduce((legTotal, leg) => {
        const legLayover = leg.layover_time.reduce(
          (sum, value) => sum + value,
          0,
        );
        return legTotal + legLayover;
      }, 0);

    const compareFlights = (a: IFlight, b: IFlight) => {
      const payableDiff = a.fare.payable - b.fare.payable;
      if (payableDiff !== 0) {
        return payableDiff;
      }

      return getTotalLayoverTime(a) - getTotalLayoverTime(b);
    };

    setFlightData((prevData) => {
      // Keep sorted by payable asc, then total layover asc for equal prices.
      const newData = [...prevData];
      let low = 0;
      let high = newData.length;

      while (low < high) {
        const mid = (low + high) >>> 1;
        if (compareFlights(newData[mid], newFlight) <= 0) {
          low = mid + 1;
        } else {
          high = mid;
        }
      }

      newData.splice(low, 0, newFlight);
      return newData;
    });
  }, []);

  // Initialize SSE connection - only when search key changes or refetch is triggered
  useEffect(() => {
    // If we already have results for this exact search, skip SSE
    const hasCachedResults = storedSearchKey === currentSearchKey && isResponseEnd;
    if (hasCachedResults) {
      return;
    }

    // If this exact attempt already failed, don't auto-retry in a loop.
    // A retry is allowed when form data changes (search key changes) or
    // when connectionKey changes (manual refetch).
    if (failedAttemptRef.current === currentAttemptKey) {
      return;
    }

    // Reset state for new search
    activeAttemptRef.current = currentAttemptKey;
    completedRef.current = false;
    hasHandledErrorRef.current = false;
    queueMicrotask(() => {
      setFlightData([]);
      setSearchInfo(null);
    });
    dispatch(resetSelectedFlight());
    dispatch(setIsResponseEnd(false));
    dispatch(setLoading(true));
    dispatch(setError(false));
    dispatch(resetFlightFilter());
    dispatch(setFilters({ type: "CHEAPEST" }));
    dispatch(setFlightTimer(undefined));

    const url = `${absoluteUrl("/b2c/flight/search/sse")}?${currentSearchKey}`;

    // Create new EventSource
    const eventSource = new EventSource(url);
    eventSourceRef.current = eventSource;

    // Search info handler
    const handleSearchInfo = (event: MessageEvent) => {
      try {
        const info = JSON.parse(event.data) as ISearchInfo;
        setSearchInfo(info);
      } catch {
        // Don't show toast for parse errors
      }
    };

    // Message handler
    const handleMessage = (event: MessageEvent) => {
      try {
        // Skip empty messages
        if (!event.data || event.data.trim() === "") return;

        const newData = JSON.parse(event.data) as IFlight;
        processFlightData(newData);
      } catch {
        // Don't show toast for parse errors
      }
    };

    // End handler
    const handleEnd = () => {
      completedRef.current = true; // Mark as completed
      failedAttemptRef.current = null;
      setTimeout(() => {
        dispatch(setIsResponseEnd(true));
        dispatch(setLoading(false));
        dispatch(setError(false));
        dispatch(toggleModifyBar(false));
        // Save the search key so we can skip SSE on remount
        dispatch(setSearchKey(currentSearchKey));
        cleanup();
      }, 1000);
    };

    // Timer handler
    const handleTimer = (event: MessageEvent) => {
      try {
        const parsed = JSON.parse(event.data) as { ttl?: number }
        if (typeof parsed.ttl === "number" && parsed.ttl > 0) {
          dispatch(setFlightTimer(Date.now() + parsed.ttl * 1000));
        }
      } catch {
        // Ignore parse errors
      }
    };

    // Set up event listeners
    eventSource.addEventListener(
      "search_info",
      handleSearchInfo as EventListener,
    );
    eventSource.addEventListener("timer", handleTimer as EventListener);
    eventSource.addEventListener("end", handleEnd as EventListener);
    eventSource.onmessage = handleMessage;

    // Error handler
    eventSource.onerror = () => {
      // Only handle error if connection wasn't intentionally closed
      if (
        eventSource.readyState !== EventSource.CLOSED &&
        !completedRef.current
      ) {
        handleError("Connection to flight search service failed");
      }
    };

    return cleanup;
  }, [
    currentSearchKey,
    dispatch,
    handleError,
    processFlightData,
    cleanup,
    connectionKey,
    currentAttemptKey,
    storedSearchKey,
    isResponseEnd,
  ]);

  // Update Redux store when flight data changes
  useEffect(() => {
    if (debouncedFlightData.length > 0) {
      dispatch(setFlightResult({ flightResult: debouncedFlightData, searchInfo }));
      dispatch(setLoading(false));
      // dispatch(resetGlobalLoading());
    }
  }, [debouncedFlightData, searchInfo, dispatch]);

  const isCacheHit =
    storedSearchKey === currentSearchKey && isResponseEnd && isCacheStillValid;

  return { flightData, searchInfo, isCacheHit };
};

export default useFlightSearchSSE;




